import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUp, Mail, Phone, MapPin } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_revenue-flow-14/artifacts/7y9pwb5v_ChatGPT%20Image%20Jan%2023%2C%202026%20at%2012_25_42%20PM.png";

const footerLinks = [
  {
    id: 'footer-services',
    title: 'Services',
    links: [
      { id: 'svc-funnel', label: 'Funnel Design' },
      { id: 'svc-automation', label: 'Marketing Automation' },
      { id: 'svc-leadgen', label: 'Lead Generation' },
      { id: 'svc-conversion', label: 'Conversion Optimization' }
    ]
  },
  {
    id: 'footer-company',
    title: 'Company',
    links: [
      { id: 'comp-about', label: 'About' },
      { id: 'comp-cases', label: 'Case Studies' },
      { id: 'comp-blog', label: 'Blog' },
      { id: 'comp-contact', label: 'Contact' }
    ]
  },
  {
    id: 'footer-resources',
    title: 'Resources',
    links: [
      { id: 'res-audit', label: 'Free Audit' },
      { id: 'res-guide', label: 'Marketing Guide' },
      { id: 'res-calc', label: 'ROI Calculator' },
      { id: 'res-faq', label: 'FAQ' }
    ]
  },
];

export default function Footer() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  return (
    <footer 
      data-testid="footer"
      className="relative pt-24 pb-12 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[#030303]" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />
      
      <div className="container-custom relative z-10">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <motion.img
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              src={LOGO_URL}
              alt="Zahar Lab"
              className="h-12 w-auto mb-6"
            />
            <p className="text-gray-400 text-sm leading-relaxed mb-6 max-w-sm">
              Building systematic marketing funnels that transform traffic into 
              predictable, scalable revenue growth.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <a href="mailto:karim@zaharlab.com" className="flex items-center gap-3 text-gray-400 text-sm hover:text-cyan-400 transition-colors">
                <Mail className="w-4 h-4" />
                <span>karim@zaharlab.com</span>
              </a>
              <a href="tel:+14385030728" className="flex items-center gap-3 text-gray-400 text-sm hover:text-cyan-400 transition-colors">
                <Phone className="w-4 h-4" />
                <span>+1 (438) 503-0728</span>
              </a>
              <a href="https://www.zaharlab.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-gray-400 text-sm hover:text-cyan-400 transition-colors">
                <MapPin className="w-4 h-4" />
                <span>www.zaharlab.com</span>
              </a>
            </div>
          </div>
          
          {/* Link Columns */}
          {footerLinks.map((column) => (
            <div key={column.id}>
              <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">
                {column.title}
              </h4>
              <ul className="space-y-3">
                {column.links.map((link) => (
                  <li key={link.id}>
                    <a 
                      href="#" 
                      className="text-gray-400 text-sm hover:text-white transition-colors"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        
        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-xs">
              © {new Date().getFullYear()} Zahar Lab. All rights reserved.
            </p>
            
            <div className="flex items-center gap-6">
              <a href="#" className="text-gray-500 text-xs hover:text-white transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-gray-500 text-xs hover:text-white transition-colors">
                Terms of Service
              </a>
            </div>
            
            {/* Back to top */}
            <motion.button
              data-testid="back-to-top"
              onClick={scrollToTop}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.95 }}
              className="w-10 h-10 rounded-full glass flex items-center justify-center hover:bg-white/10 transition-colors"
            >
              <ArrowUp className="w-5 h-5 text-gray-400" />
            </motion.button>
          </div>
        </div>
      </div>
    </footer>
  );
}
